/**
 * Request Deduplication Tracker (Axios-level)
 *
 * Prevents duplicate API calls by caching in-flight request promises.
 * If the same request (same URL + payload) is already pending,
 * returns the same promise instead of making a new HTTP request.
 *
 * This solves the issue of React 18 StrictMode double-mounting
 * and multiple useEffects triggering duplicate API calls.
 */

// Store for tracking pending request promises
const pendingRequests = new Map();

/**
 * Creates a unique key for a request based on method, URL and payload
 * @param {object} config - Axios request config
 * @returns {string} - Unique request key
 */
export const createRequestKey = (config) => {
  try {
    const { method, url, data } = config;
    // Create a stable key from method, URL and sorted payload
    const dataString = typeof data === "string" ? data : JSON.stringify(data || {});
    return `${method}::${url}::${dataString}`;
  } catch (e) {
    // Fallback for circular references or other stringify issues
    return `${config.method}::${config.url}::${Date.now()}`;
  }
};

/**
 * Get cached promise for a pending request
 * @param {string} requestKey - The unique request key
 * @returns {Promise|null} - Cached promise or null
 */
export const getPendingRequest = (requestKey) => {
  const pending = pendingRequests.get(requestKey);
  return pending?.promise || null;
};

/**
 * Register a request promise as pending
 * @param {string} requestKey - The unique request key
 * @param {Promise} promise - The axios request promise
 * @param {number} timeout - Auto-cleanup timeout in ms (default: 30s)
 */
export const registerRequest = (requestKey, promise, timeout = 30000) => {
  // Setup auto-cleanup for stale requests
  const timeoutId = setTimeout(() => {
    pendingRequests.delete(requestKey);
  }, timeout);

  pendingRequests.set(requestKey, {
    promise,
    timestamp: Date.now(),
    timeoutId
  });
};

/**
 * Unregister a request (call on completion)
 * @param {string} requestKey - The unique request key
 */
export const unregisterRequest = (requestKey) => {
  const request = pendingRequests.get(requestKey);
  if (request?.timeoutId) {
    clearTimeout(request.timeoutId);
  }
  pendingRequests.delete(requestKey);
};

/**
 * Clear all pending requests (useful for logout/cleanup)
 */
export const clearAllPendingRequests = () => {
  pendingRequests.forEach((request) => {
    if (request?.timeoutId) {
      clearTimeout(request.timeoutId);
    }
  });
  pendingRequests.clear();
};

/**
 * Get count of pending requests (for debugging)
 * @returns {number} - Number of pending requests
 */
export const getPendingRequestCount = () => {
  return pendingRequests.size;
};

/**
 * Debug: Log all pending requests
 */
export const logPendingRequests = () => {
  if (process.env.NODE_ENV === "development") {
    console.log("Pending Requests:", Array.from(pendingRequests.keys()));
  }
};

export default {
  createRequestKey,
  getPendingRequest,
  registerRequest,
  unregisterRequest,
  clearAllPendingRequests,
  getPendingRequestCount,
  logPendingRequests
};
